import {FC, useEffect, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {getActiveByUserType} from '../../../UI/master-data/cancellation-reason-management/list/core/_requests'
import {CancellationReasonShort} from '../../../UI/master-data/cancellation-reason-management/list/core/_models'

type CancelConfirmationModalProps = {
  onSubmit: (payload: any) => Promise<boolean | void>
  title: string
  content: string
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  isConfirmed?: boolean
  variant?: 'admin' | 'client'
  allowAdvancedCancellationTypes?: boolean
}

const CancelConfirmationModal: FC<CancelConfirmationModalProps> = ({
  onSubmit,
  title,
  setOpen,
  isConfirmed = false,
  variant = 'admin',
  allowAdvancedCancellationTypes = true,
}) => {
  const isClient = variant === 'client'
  const [isSubmitting, setSubmitting] = useState(false)
  const [cancellationType, setCancellationType] = useState('normal')
  const [cancellationReasonId, setCancellationReasonId] = useState<number | null>(null)
  const [cancellationReasons, setCancellationReasons] = useState<CancellationReasonShort[]>([])
  const [reasonsLoading, setReasonsLoading] = useState(false)
  const [payLinguist, setPayLinguist] = useState(false)
  const [chargeClient, setChargeClient] = useState(false)
  const [payableHours, setPayableHours] = useState('')
  const [payableFull, setPayableFull] = useState(false)
  const [cancellationDetails, setCancellationDetails] = useState('')

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  useEffect(() => {
    const fetchCancellationReasons = async () => {
      setReasonsLoading(true)
      try {
        const reasons = await getActiveByUserType(isClient ? 'Client' : 'Internal')
        setCancellationReasons(reasons)
      } catch (error) {
        console.error('Error fetching cancellation reasons:', error)
      } finally {
        setReasonsLoading(false)
      }
    }
    fetchCancellationReasons()
  }, [isClient])

  const handleSubmit = async () => {
    if (!cancellationReasonId) {
      return
    }

    setSubmitting(true)

    let payload: any = {
      cancellationDetails: cancellationDetails,
      fK_CancellationReason: cancellationReasonId,
    }

    if (!isClient) {
      if (cancellationType === 'linguist') {
        payload.noLinguist = true
      } else if (cancellationType === 'invoice') {
        payload.payable = payLinguist
        payload.billable = chargeClient
        payload.payableHours = payableFull ? null : payableHours ? parseInt(payableHours) : null
        payload.payableFull = payableFull
      }
    }

    try {
      const success = await onSubmit(payload)
      if (success !== false) {
        setOpen(false)
      }
    } catch (error) {
      console.error('Error submitting cancellation:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handlePayableFullChange = (checked: boolean) => {
    setPayableFull(checked)
    if (checked) {
      setPayableHours('')
    }
  }

  return (
    <>
      <div
        className='modal fade show d-block'
        id='kt_modal_cancel_confirmation'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
      >
        <div className='modal-dialog modal-dialog-centered mw-600px'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2 className='fw-bolder'>{title}</h2>
              <div
                className='btn btn-icon btn-sm btn-active-icon-primary'
                data-kt-users-modal-action='close'
                style={{cursor: 'pointer'}}
                onClick={() => setOpen(false)}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </div>
            </div>

            <div className='modal-body'>
              {!isClient && allowAdvancedCancellationTypes && (
                <div className='mb-4'>
                  <label className='form-label fw-bold'>Cancellation Type</label>
                  <select
                    className='form-select'
                    value={cancellationType}
                    onChange={(e) => setCancellationType(e.target.value)}
                  >
                    <option value='normal'>Normal Cancellation</option>
                    {isConfirmed && <option value='linguist'>Linguist No Show</option>}
                    <option value='invoice'>Invoice Applicable</option>
                  </select>
                </div>
              )}

              <div className='mb-4'>
                <label className='form-label fw-bold required'>Cancellation Reason</label>
                {isClient && !reasonsLoading && cancellationReasons.length === 0 ? (
                  <p className='text-muted mb-0'>
                    No cancellation reasons are available. Please contact Ad Astra support for
                    assistance.
                  </p>
                ) : (
                  <>
                    <select
                      className={`form-select ${!cancellationReasonId ? 'is-invalid' : ''}`}
                      value={cancellationReasonId || ''}
                      onChange={(e) =>
                        setCancellationReasonId(e.target.value ? Number(e.target.value) : null)
                      }
                      disabled={reasonsLoading}
                    >
                      <option value=''>Select a reason...</option>
                      {cancellationReasons.map((reason) => (
                        <option key={reason.code} value={reason.code}>
                          {reason.name}
                        </option>
                      ))}
                    </select>
                    {!cancellationReasonId && (
                      <div className='invalid-feedback'>Please select a cancellation reason</div>
                    )}
                  </>
                )}
              </div>

              {!isClient && allowAdvancedCancellationTypes && cancellationType === 'invoice' && (
                <div className='mb-4'>
                  <div className='border border-dashed border-gray-300 rounded p-3'>
                    <div className='d-flex gap-4'>
                      <div className='form-check'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='payLinguist'
                          checked={payLinguist}
                          onChange={(e) => setPayLinguist(e.target.checked)}
                          disabled={!isConfirmed}
                        />
                        <label
                          className={`form-check-label fw-bold${!isConfirmed ? ' text-muted' : ''}`}
                          htmlFor='payLinguist'
                        >
                          Pay Linguist
                        </label>
                      </div>
                      <div className='form-check'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='chargeClient'
                          checked={chargeClient}
                          onChange={(e) => setChargeClient(e.target.checked)}
                        />
                        <label className='form-check-label fw-bold' htmlFor='chargeClient'>
                          Charge Client
                        </label>
                      </div>
                    </div>
                  </div>

                  {payLinguist && (
                    <div className='mt-3'>
                      <div className='d-flex align-items-center gap-3 mb-2'>
                        <span className='fw-bold'>Payable Hour(s):</span>
                        <i className='bi bi-info-circle text-primary'></i>
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='payableFull'
                            checked={payableFull}
                            onChange={(e) => handlePayableFullChange(e.target.checked)}
                          />
                          <label className='form-check-label fw-bold' htmlFor='payableFull'>
                            Payable Full
                          </label>
                        </div>
                      </div>
                      {!payableFull && (
                        <input
                          type='number'
                          className='form-control'
                          placeholder='Enter payable hours'
                          value={payableHours}
                          onChange={(e) => setPayableHours(e.target.value)}
                          min='0'
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className='mb-4'>
                <label className='form-label fw-bold'>Cancellation Details</label>
                <textarea
                  className='form-control'
                  rows={4}
                  placeholder='Text box'
                  value={cancellationDetails}
                  onChange={(e) => setCancellationDetails(e.target.value)}
                />
              </div>

              <div className='d-flex justify-content-end gap-3'>
                <button
                  className='btn btn-light'
                  onClick={() => setOpen(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    !cancellationReasonId ||
                    reasonsLoading ||
                    (isClient && cancellationReasons.length === 0)
                  }
                  className='btn btn-success'
                >
                  {isSubmitting ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

export {CancelConfirmationModal}
